import { useAppSettingStore } from '@/client/store/app-store';
import { api } from '@/client/utils/api';
import { useMarkets } from '@/libs/react-sdk/api/use-markets';
import { MarketsSortType, type MarketsParams } from '@/libs/sdk/api';

// Volume-ranked markets for the current lens. Exported so every consumer
// (and test) builds the searchParams object the exact same way -- that is
// what makes their react-query keys match and collapse into one request.
export const buildTrendingMarketsParams = (baseTokenAddress: string): MarketsParams => ({
  page: 1,
  pageSize: 24,
  sortType: MarketsSortType.volume,
  sortAsc: false,
  baseTokenAddress,
});

// Pure gate: the search palette's recommendations fetch only while the
// palette is actually open, never eagerly on app load. Extracted so the
// gating decision is unit-testable without mounting the Dialog that owns
// the open state.
export const shouldFetchSearchRecommend = (isOpen: boolean): boolean => isOpen;

// Shared volume-sorted list -- home "Trending now" rail + search palette
// recommendations. One key, one request: opening search on the home page
// reuses the rail's cache entry instead of firing its own query (the old
// search-recommend query used a distinct params shape, so it could never
// share cache with anything).
export const useTrendingMarkets = (options?: { enabled?: boolean }) => {
  const lensTokenAddress = useAppSettingStore(state => state.baseToken.address);
  return useMarkets(api, buildTrendingMarketsParams(lensTokenAddress), {
    enabled: options?.enabled,
    // Without this, react-query's default staleTime of 0 marks the rail's
    // data stale immediately, so opening the search palette always fires a
    // background refetch of the same key. 30s keeps palette opens a pure
    // cache hit while trending prices still refresh often enough.
    staleTime: 30_000,
  });
};
